import { IGetLatestPaymentInfoResponse } from '@/shared/api/billing/types';
import { ALL_ATTRIBUTION_KEYS } from '@/shared/constants/attribution';
import { getStoredAttributionParams } from '@/shared/lib/attribution';
import { isTrackingDisabled } from '@/shared/lib/isTrackingDisabled';
import { User } from '@/shared/types/User';

import { AdsEvents } from '../ads';
import { IS_GTM_DEBUGGER_ENABLED } from './config';
import { BeginCheckoutEventData, RegistrationSurveyEventData, ViewItemListEventData } from './types';

window.dataLayer = window.dataLayer || [];

export const pushEvent = ({ eventName, properties }: { eventName: string; properties?: object }) => {
  if (isTrackingDisabled()) return;

  window.dataLayer.push({
    event: eventName,
    ...properties,
  });
  if (IS_GTM_DEBUGGER_ENABLED) {
    console.info('%cGTM debugger event:', 'color: #F79009');
    console.info({ eventName, ...properties });
  }
};

const TRIAL_FUNNEL_EVENTS = new Set([
  'anyResultDownloaded',
  'bookACallBooked',
  'bookSalesDemoBooked',
  'downloadVideo',
  'dubButtonPressed',
  'leadSurveyQualified',
  'trialPreviewViewed',
  'videoIsGenerated',
]);

export const mirrorTrialFunnelEvent = (eventName: string, hasTrialPlan?: boolean, properties?: object) => {
  if (hasTrialPlan && TRIAL_FUNNEL_EVENTS.has(eventName)) {
    pushEvent({ eventName, properties });

    if (eventName === 'trialPreviewViewed') {
      const projectId = properties && 'projectId' in properties ? String(properties.projectId) : undefined;
      AdsEvents.trialPreviewViewed(projectId);
    }
  }
};

export const pushVariables = (variables: { [key: string]: string | undefined }) => {
  if (isTrackingDisabled()) return;

  window.dataLayer.push({
    ...variables,
  });
  if (IS_GTM_DEBUGGER_ENABLED) {
    console.info('%cGTM debugger set variables:', 'color: #F79009');
    console.info({ ...variables });
  }
};

/**
 * Push the filtered acquisition snapshot before application events. The inline
 * pre-GTM bootstrap already did this for the first pageview; this keeps runtime
 * events and post-OAuth routes on the same values.
 */
export const pushAttributionToDataLayer = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const attribution: Record<string, string> = { ...getStoredAttributionParams() };

  for (const key of ALL_ATTRIBUTION_KEYS) {
    const value = urlParams.get(key);
    if (value) attribution[key] = value;
  }

  if (Object.keys(attribution).length > 0) {
    pushVariables(attribution);
  }
};

export const GTMEvents = {
  beginCheckout: (eventData: BeginCheckoutEventData) => {
    pushEvent({ eventName: 'begin_checkout', properties: eventData });
    AdsEvents.beginCheckout(eventData);
  },

  customerSurvey: (eventData: RegistrationSurveyEventData) => {
    pushEvent({ eventName: 'customerSurvey', properties: eventData });
  },

  identifyUser: (user: Pick<User, 'country' | 'email' | 'id'>) => AdsEvents.identifyUser(user),

  login: () => {
    pushEvent({ eventName: 'login' });
  },

  purchase: (latestPaymentInfo: IGetLatestPaymentInfoResponse) => {
    pushEvent({ eventName: 'purchase', properties: { ecommerce: latestPaymentInfo } });
    AdsEvents.purchase(latestPaymentInfo);
  },

  registration: (userId?: User['id']) => {
    pushEvent({ eventName: 'registration' });
    AdsEvents.signUp(userId);
  },

  signUpPageShown: () => {
    pushEvent({ eventName: 'signUpPageShown' });
  },

  subscriptionPageOpened: () => {
    pushEvent({ eventName: 'subscriptionPageOpened' });
  },

  videoUpload: () => {
    pushEvent({ eventName: 'videoUpload' });
  },

  viewItemList: (eventData: ViewItemListEventData) => {
    pushEvent({ eventName: 'view_item_list', properties: eventData });
  },

  setUserId: (userId?: User['id']) => {
    pushVariables({ user_id: userId });
  },
};
